import { useState } from "react";
import { useLoader } from "@/hooks/LoaderProvider";
import { useLocation, useNavigate } from "react-router-dom";
import Button from "@/components/common/Button";
import { ArrowRightOutlined } from "@/config/antd";
import { useDispatch, useSelector } from "react-redux";
import {
  verifyOtpAsync,
  sendLoginOtpAsync,
  selectUserInfo,
} from "@/store/redux/slices/auth/auth_slice";
import Copyright from "@/components/layout/Footer/Copyright";
import Success from "@/components/common/alerts/Success";
import Error from "@/components/common/alerts/Error";
import AuthHeader from "@/components/layout/Header/Auth_Header";
import OtpInput from "@/components/common/inputs/Otp_Input";

const FLOW_CONFIG = {
  signup: {
    title: "Setup a New Account",
    description:
      "Welcome to our service. We're thrilled that you're interested in using our service.",
    showEmail: false,
    buttonText: "Verify OTP",
    successParagraph:
      'Your account has been successfully confirmed. Click "Continue" to onboard yourself.',
    successPath: "/account-setup-step-1",
    titleAlign: "text-left",
  },
  login: {
    title: "Confirm Account",
    description: "We sent an OTP to your email address.",
    showEmail: true,
    buttonText: "Proceed",
    successParagraph:
      'Your account has been successfully confirmed. Click "Continue" to proceed.',
    successPath: "/dashboard",
    titleAlign: "text-center",
  },
};

const EMPTY_OTP = ["", "", "", "", "", ""];

const VerifyOtp = () => {
  const { showLoader, hideLoader } = useLoader();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const userInfo = useSelector(selectUserInfo);

  const flow = location.pathname.startsWith("/2fa") ? "login" : "signup";
  const config = FLOW_CONFIG[flow];
  const userEmail = userInfo?.email;

  const [otp, setOtp] = useState(EMPTY_OTP);
  const [isSuccessVisible, setIsSuccessVisible] = useState(false);
  const [isErrorVisible, setIsErrorVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isResending, setIsResending] = useState(false);
  const [resendMessage, setResendMessage] = useState("");

  const isFormFilled = otp.every((digit) => digit !== "");

  const handleProceed = async () => {
    if (!isFormFilled) return;

    showLoader();

    try {
      await dispatch(
        verifyOtpAsync({
          otp,
          userID: userInfo.userID,
        })
      ).unwrap();

      hideLoader();
      setIsSuccessVisible(true);
    } catch (err) {
      hideLoader();
      const message =
        err?.message || err?.error || "Invalid OTP. Please try again.";
      setErrorMessage(message);
      setIsErrorVisible(true);
    }
  };

  const handleResendOtp = async () => {
    if (!userInfo?.userID || isResending) return;

    setIsResending(true);
    setResendMessage("");
    try {
      await dispatch(sendLoginOtpAsync(userInfo.userID)).unwrap();
      setResendMessage("A new OTP has been sent to your email.");
      setOtp(EMPTY_OTP);
    } catch (err) {
      setErrorMessage(
        err?.message || err?.error || "Failed to resend OTP. Please try again."
      );
      setIsErrorVisible(true);
    } finally {
      setIsResending(false);
    }
  };

  const handleCloseSuccess = () => {
    setIsSuccessVisible(false);
  };

  const handleCloseError = () => {
    setIsErrorVisible(false);
    setErrorMessage("");
  };

  const handleContinue = () => {
    showLoader();
    setTimeout(() => {
      hideLoader();
      setIsSuccessVisible(false);
      navigate(config.successPath);
    }, 2000);
  };

  return (
    <div className="flex flex-col h-screen bg-white">
      <AuthHeader
        logoName="CMSLogo"
        logoSize="large"
        showTitle={false}
        className="p-0"
        containerClassName="px-4 md:px-6 py-4"
        showUserGreeting={true}
        avatarSize={40}
      />

      <div className="flex flex-col w-full max-w-md px-4 mx-auto mt-8 md:px-6">
        <h1
          className={`mb-4 heading-large whitespace-nowrap text-primary ${config.titleAlign}`}>
          {config.title}
        </h1>

        {config.showEmail ? (
          <div className="w-full mb-6 text-center">
            <p className="text-small text-darkGrey mb-1">{config.description}</p>
            <p className="text-small text-darkGrey font-bold">{userEmail}</p>
          </div>
        ) : (
          <p className="w-full mb-6 text-left text-small text-darkGrey">
            {config.description}
          </p>
        )}

        <OtpInput value={otp} onChange={setOtp} />

        <p className="mt-3 mb-4 text-center text-small text-darkGrey">
          Didn&apos;t receive the code?{" "}
          <button
            type="button"
            onClick={handleResendOtp}
            disabled={isResending || !userInfo?.userID}
            className="font-semibold text-primary hover:underline disabled:opacity-50 disabled:cursor-not-allowed bg-transparent border-none p-0 cursor-pointer">
            {isResending ? "Sending..." : "Resend OTP"}
          </button>
        </p>

        {resendMessage ? (
          <p className="mb-4 text-center text-small text-primary">
            {resendMessage}
          </p>
        ) : null}

        <div>
          <Button
            text={config.buttonText}
            icon={<ArrowRightOutlined />}
            onClick={handleProceed}
            isActive={isFormFilled}
            color="darkBlue"
            className={`w-full ${
              isFormFilled ? "bg-primary text-white" : "bg-darkBlue text-primary"
            }`}
          />
        </div>

        <div className="mt-8">
          <Copyright />
        </div>
      </div>

      <Success
        isVisible={isSuccessVisible}
        onClose={handleCloseSuccess}
        header="OTP Correct"
        paragraph={config.successParagraph}
        buttonText="Continue"
        onButtonClick={handleContinue}
      />

      <Error
        isVisible={isErrorVisible}
        onClose={handleCloseError}
        header="Verification Failed"
        paragraph={errorMessage}
        buttonText="Try Again"
      />
    </div>
  );
};

export default VerifyOtp;
